import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { papers, readPapers } from '../data/papers';

const ResearchPapers: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<'publications' | 'reading'>('publications');

  return (
    <div className={`min-h-screen py-20 md:py-32 ${isDarkMode ? 'bg-black' : 'bg-stone-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className={`font-serif text-4xl md:text-5xl font-normal mb-4 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>
              Research Papers
            </h1>
            <p className={`font-serif text-lg md:text-xl ${isDarkMode ? 'text-gray-300' : 'text-stone-600'}`}>
              Exploring the latest publications in computational biology, AI, and biomedical engineering.
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-12 flex gap-4 md:gap-8 overflow-x-auto pb-4 border-b border-stone-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('publications')}
              className={`text-base md:text-lg whitespace-nowrap transition-all ${
                activeTab === 'publications'
                  ? isDarkMode
                    ? 'text-white border-b-2 border-white pb-1'
                    : 'text-stone-900 border-b-2 border-stone-900 pb-1'
                  : isDarkMode
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
              style={{ fontFamily: 'Georgia, serif' }}
            >
              My Publications
            </button>
            <button
              onClick={() => setActiveTab('reading')}
              className={`text-base md:text-lg whitespace-nowrap transition-all ${
                activeTab === 'reading'
                  ? isDarkMode
                    ? 'text-white border-b-2 border-white pb-1'
                    : 'text-stone-900 border-b-2 border-stone-900 pb-1'
                  : isDarkMode
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Reading List
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-8">
            {activeTab === 'publications' && papers.map((paper) => (
              <motion.div
                key={paper.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                className={`w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${
                  isDarkMode ? 'border-gray-700' : 'border-stone-200'
                } p-6 rounded-lg`}
              >
                <div className="flex flex-col h-full">
                  <h3 className={`font-serif text-2xl md:text-3xl font-normal mb-3 ${
                    isDarkMode ? 'text-white' : 'text-stone-900'
                  }`}>
                    {paper.title}
                  </h3>
                  
                  <div className={`font-serif text-sm md:text-base italic mb-4 ${
                    isDarkMode ? 'text-gray-400' : 'text-stone-600'
                  }`}>
                    {paper.authors}
                  </div>

                  <div className={`font-serif text-sm md:text-base font-semibold mb-6 ${
                    isDarkMode ? 'text-stone-300' : 'text-stone-700'
                  }`}>
                    {paper.venue} • {paper.date}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {paper.keywords.map((keyword) => (
                      <span
                        key={keyword}
                        className={`text-sm px-3 py-1 ${
                          isDarkMode
                            ? 'bg-gray-700 text-gray-200'
                            : 'bg-stone-100 text-stone-700'
                        } rounded-full`}
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>

                  <div className={`font-serif text-base mb-6 ${
                    isDarkMode ? 'text-gray-300' : 'text-stone-800'
                  }`}>
                    <strong>TL;DR:</strong> {paper.tldr}
                  </div>

                  <p className={`font-serif text-sm md:text-base leading-relaxed ${
                    isDarkMode ? 'text-gray-400' : 'text-stone-600'
                  } mb-6`}>
                    <strong>Abstract:</strong> {paper.abstract}
                  </p>

                  <div className="mt-auto">
                    <a
                      href={paper.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-block font-serif text-base px-6 py-2 rounded-full border transition-colors ${
                        isDarkMode 
                          ? 'border-gray-500 text-gray-300 hover:bg-gray-700' 
                          : 'border-stone-300 text-stone-700 hover:bg-stone-100'
                      }`}
                    >
                      Read Paper
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}

            {activeTab === 'reading' && readPapers.map((paper) => (
              <motion.div
                key={paper.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                className={`w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${
                  isDarkMode ? 'border-gray-700' : 'border-stone-200'
                } p-6 rounded-lg`}
              >
                <div className="flex flex-col h-full">
                  <h3 className={`font-serif text-xl md:text-2xl font-normal mb-2 ${
                    isDarkMode ? 'text-white' : 'text-stone-900'
                  }`}>
                    {paper.title}
                  </h3>
                  
                  <div className={`font-serif text-sm md:text-base italic mb-4 ${
                    isDarkMode ? 'text-gray-400' : 'text-stone-600'
                  }`}>
                    {paper.authors} • Read in {paper.dateRead}
                  </div>

                  <div className={`font-serif text-base leading-relaxed mb-6 ${
                    isDarkMode ? 'text-gray-300' : 'text-stone-700'
                  }`}>
                    <strong>My Views:</strong> {paper.review}
                  </div>

                  {paper.takeaways && paper.takeaways.length > 0 && (
                    <div className="mb-6">
                      <strong className={`font-serif text-sm md:text-base mb-2 block ${isDarkMode ? 'text-gray-300' : 'text-stone-800'}`}>
                        Key Takeaways:
                      </strong>
                      <ul className={`list-disc pl-5 font-serif text-sm md:text-base ${isDarkMode ? 'text-gray-400' : 'text-stone-600'} space-y-1`}>
                        {paper.takeaways.map((takeaway, idx) => (
                          <li key={idx}>{takeaway}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-auto">
                    <a
                      href={paper.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-block font-serif text-sm px-5 py-2 rounded-full border transition-colors ${
                        isDarkMode 
                          ? 'border-gray-500 text-gray-300 hover:bg-gray-700' 
                          : 'border-stone-300 text-stone-700 hover:bg-stone-100'
                      }`}
                    >
                      View Source
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResearchPapers;